import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

// Bewusst schlank gehalten (siehe Task-6-Brief): kein 1:1-Abbild von
// AiChatMessage aus dem GraphQL-Schema, sondern nur das, was die Chat-UI
// zum Rendern braucht.
export type ChatMessageRole = 'user' | 'assistant'

export type ChatDrawerPosition = 'left' | 'right' | 'bottom'

// 'overlay': Drawer liegt über dem Inhalt. 'push': der Hauptinhalt weicht
// per Margin aus (angewendet im ContextWrapper).
export type ChatDrawerMode = 'overlay' | 'push'

export type ChatDrawerLayout = {
  position: ChatDrawerPosition
  width: number
  height: number
  mode: ChatDrawerMode
}

export const DEFAULT_DRAWER_LAYOUT: ChatDrawerLayout = {
  position: 'right',
  width: 500,
  height: 440,
  mode: 'overlay'
}

// localStorage ist User-Input: kaputte/veraltete Werte dürfen das Layout
// nicht zerlegen, daher werden sie beim Rehydrieren normalisiert.
export const sanitizeDrawerLayout = (value: unknown): ChatDrawerLayout => {
  const layout = (value ?? {}) as Partial<ChatDrawerLayout>
  return {
    position: ['left', 'right', 'bottom'].includes(layout.position as string)
      ? (layout.position as ChatDrawerPosition)
      : DEFAULT_DRAWER_LAYOUT.position,
    width: Number.isFinite(layout.width) ? (layout.width as number) : DEFAULT_DRAWER_LAYOUT.width,
    height: Number.isFinite(layout.height)
      ? (layout.height as number)
      : DEFAULT_DRAWER_LAYOUT.height,
    mode: layout.mode === 'push' ? 'push' : DEFAULT_DRAWER_LAYOUT.mode
  }
}

export type ChatStatus = 'idle' | 'waiting' | 'error'

export type ChatMessageUpload = {
  filename: string
  url?: string | null
}

export type ChatMessage = {
  id: string
  role: ChatMessageRole
  content: string
  status?: string
  createdAt?: string
  uploads?: ChatMessageUpload[]
}

type ChatState = {
  isOpen: boolean
  conversationId: string | null
  messages: ChatMessage[]
  status: ChatStatus
  // true, sobald der Store einmal serverseitig hydratisiert wurde (Query
  // myActiveAiChatConversation) ODER der Client den Serverzustand ohnehin
  // kennt (nach reset()). Session-flüchtig, bewusst NICHT persistiert.
  hydrated: boolean
  layout: ChatDrawerLayout
  // Vom User gewählter Chat-Agent für die NÄCHSTE Konversation; null =
  // Standard-Agent. Persistiert wie layout (User-Präferenz).
  selectedAgentId: string | null
}

type ChatStore = ChatState & {
  open: () => void
  close: () => void
  setConversationId: (conversationId: string | null) => void
  // Dedupliziert per Message-Id, damit derselbe ActionCable-Broadcast (oder
  // eine Query-Nachladung nach Reconnect) nicht zu doppelten Einträgen führt.
  addMessage: (message: ChatMessage) => void
  setStatus: (status: ChatStatus) => void
  setHydrated: () => void
  setLayout: (layout: Partial<ChatDrawerLayout>) => void
  setSelectedAgentId: (selectedAgentId: string | null) => void
  reset: () => void
}

const initialState: ChatState = {
  isOpen: false,
  conversationId: null,
  messages: [],
  status: 'idle',
  hydrated: false,
  layout: DEFAULT_DRAWER_LAYOUT,
  selectedAgentId: null
}

export const useChatStore = create<ChatStore>()(
  devtools(
    persist(
      set => ({
        ...initialState,

        open: () => set({ isOpen: true }),
        close: () => set({ isOpen: false }),

        setConversationId: conversationId => set({ conversationId }),

        addMessage: message =>
          set(state => {
            // Upsert statt first-write-wins: Der connected-Resync kann eine
            // Assistant-Message bereits im Zustand pending (content leer) laden,
            // bevor der Callback-Broadcast den finalen Content liefert — der
            // spätere Stand muss die bestehende Message aktualisieren.
            if (state.messages.some(existing => existing.id === message.id)) {
              return {
                messages: state.messages.map(existing =>
                  existing.id === message.id ? { ...existing, ...message } : existing
                )
              }
            }
            return { messages: [...state.messages, message] }
          }),

        setStatus: status => set({ status }),

        setHydrated: () => set({ hydrated: true }),

        setLayout: layout => set(state => ({ layout: { ...state.layout, ...layout } })),

        setSelectedAgentId: selectedAgentId => set({ selectedAgentId }),

        // layout bleibt bei reset bewusst erhalten (User-Präferenz, keine
        // Konversationsdaten). selectedAgentId auch (User-Präferenz wie layout).
        // hydrated bleibt true: nach dem Reset kennt der Client den Serverzustand
        // (leer) — keine erneute Hydration.
        reset: () =>
          set(state => ({
            ...initialState,
            hydrated: true,
            layout: state.layout,
            selectedAgentId: state.selectedAgentId
          }))
      }),
      {
        name: 'aiChat.layout',
        partialize: state => ({ layout: state.layout, selectedAgentId: state.selectedAgentId }),
        merge: (persisted, current) => {
          const persistedState = persisted as Partial<ChatState> | undefined
          return {
            ...current,
            layout: sanitizeDrawerLayout(persistedState?.layout),
            selectedAgentId:
              typeof persistedState?.selectedAgentId === 'string'
                ? persistedState.selectedAgentId
                : null
          }
        }
      }
    ),
    {
      name: 'ai-chat-store' // für DevTools
    }
  )
)
